<!--
  Quotation Completeness Indicator
  Author: Akarsh Singh | Created on: 2026-08-17
  Updated on: 2026-08-17 — panel rendered through a CDK overlay anchored to the
                           pill that opened it; second pill added for
                           RECOMMENDED (optional) fields.
  Purpose: compact progress strip + two collapsed dropdowns, rendered under the
           "Create Quotation" hero title on every quotation step.

  The bar and the percentage count REQUIRED fields only, so 100% always means
  "I can submit this". Recommended fields sit beside it in teal and never move
  the bar.
-->
<div class="qc-strip" *ngIf="total > 0">

  <!-- Progress bar — mandatory fields only -->
  <div
    class="qc-strip__track"
    role="progressbar"
    [attr.aria-valuenow]="percent"
    aria-valuemin="0"
    aria-valuemax="100"
    [attr.aria-label]="'Required fields completed for ' + scopeLabel"
  >
    <span
      class="qc-strip__fill"
      [class.qc-strip__fill--complete]="percent === 100"
      [style.width.%]="percent"
    ></span>
  </div>

  <span class="qc-strip__percent">{{ percent }}% complete</span>

  <span class="qc-strip__divider"></span>

  <!-- Submittable, but optional fields remain. Said plainly so the teal pill
       beside it is never mistaken for something that blocks. -->
  <ng-container *ngIf="isReadyWithSuggestions">
    <span class="qc-strip__ready">
      <mat-icon>check_circle</mat-icon>
      Ready to submit
    </span>
    <span class="qc-strip__divider"></span>
  </ng-container>

  <!-- REQUIRED — orange, because it blocks submission -->
  <button
    #requiredButton
    *ngIf="requiredCount > 0"
    type="button"
    class="qc-strip__pill qc-strip__pill--required"
    [attr.aria-expanded]="openGroup === 'required'"
    aria-haspopup="true"
    (click)="togglePanel('required')"
  >
    Missing Fields
    <span class="qc-strip__badge qc-strip__badge--required">{{ requiredCount }}</span>
    <mat-icon
      class="qc-strip__caret"
      [class.qc-strip__caret--open]="openGroup === 'required'"
    >expand_more</mat-icon>
  </button>

  <!-- RECOMMENDED — teal, because it never blocks anything -->
  <button
    #recommendedButton
    *ngIf="recommendedCount > 0"
    type="button"
    class="qc-strip__pill qc-strip__pill--recommended"
    [attr.aria-expanded]="openGroup === 'recommended'"
    aria-haspopup="true"
    (click)="togglePanel('recommended')"
  >
    Recommended
    <span class="qc-strip__badge qc-strip__badge--recommended">{{ recommendedCount }}</span>
    <mat-icon
      class="qc-strip__caret"
      [class.qc-strip__caret--open]="openGroup === 'recommended'"
    >expand_more</mat-icon>
  </button>

  <!-- Nothing left to list at all, so both controls are replaced. -->
  <span *ngIf="isEverythingFilled" class="qc-strip__complete">
    <mat-icon>check_circle</mat-icon>
    Nothing missing
  </span>
</div>

<!--
  One template serves both panels; the open group decides its content and its
  colour. It is projected into a CDK overlay attached to <body> - see the
  component doc for why it cannot live inside the hero.
-->
<ng-template #panelTemplate>
  <div class="qc-panel">
    <div
      class="qc-panel__head"
      [class.qc-panel__head--recommended]="panelIsRecommended"
    >
      <span>{{ panelTitle }}</span>
      <span class="qc-panel__count">{{ panelItems.length }} remaining</span>
    </div>

    <!-- Only the recommended list needs explaining: the supplier has to know
         these are safe to skip before the benefit lines mean anything. -->
    <div class="qc-panel__why" *ngIf="panelIsRecommended">
      Optional — you can submit without these. Buyers compare quotes faster when
      they are filled, so yours gets noticed sooner.
    </div>

    <ul class="qc-panel__list">
      <li *ngFor="let field of panelItems; trackBy: trackByField">
        <button type="button" class="qc-panel__item" (click)="onFieldClick(field)">
          <span
            class="qc-panel__dot"
            [class.qc-panel__dot--recommended]="panelIsRecommended"
          ></span>

          <span class="qc-panel__body">
            <span class="qc-panel__name">{{ field.label }}</span>
            <span class="qc-panel__benefit" *ngIf="field.benefit">{{ field.benefit }}</span>
          </span>

          <span class="qc-panel__section">{{ field.section }}</span>
          <mat-icon class="qc-panel__go">chevron_right</mat-icon>
        </button>
      </li>
    </ul>
  </div>
</ng-template>
